import { FocusEvent, useEffect, useMemo } from 'react'
import { RichTextEditor as MantineRichTextEditor, Link } from '@mantine/tiptap'
import { Content, useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Highlight from '@tiptap/extension-highlight'
import Underline from '@tiptap/extension-underline'
import Placeholder from '@tiptap/extension-placeholder'
import { Box } from '@mantine/core'
import { clsx } from '@mantine/styles'

import type { RichTextEditorProps } from './rich-text-editor.types'
import {
  buildStarterKitConfig,
  getVisibleToolbarGroups,
  isControlAllowed,
  renderToolbarControl,
} from './rich-text-editor.utils'
import classes from './rich-text-editor.module.scss'

export const handleOnBlur =
  (
    handleBlur: (_: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void,
    fieldName: string
  ) =>
  ({ event }: { event: globalThis.FocusEvent }) => {
    const blurEvent = event as unknown as FocusEvent<
      HTMLInputElement | HTMLTextAreaElement
    >

    if (blurEvent?.target) {
      blurEvent.target.name = fieldName
    }
    handleBlur(blurEvent)
  }

/** Stable key so editor re-mounts when controls or readOnly change (extensions and placeholder stay in sync). */
function getEditorKey(
  controls: RichTextEditorProps['controls'],
  readOnly: boolean
): string {
  const controlsPart =
    controls === undefined ? 'full' : [...controls].sort().join(',')
  return `${controlsPart}-${readOnly}`
}

function RichTextEditorInner(props: Readonly<RichTextEditorProps>) {
  const {
    fieldName,
    placeholder,
    className,
    handleUpdate,
    handleBlur,
    content,
    error,
    bottomAddOn,
    labels,
    controls,
    readOnly = false,
    testId,
  } = props

  const starterKitConfig = useMemo(
    () => buildStarterKitConfig(controls),
    [controls]
  )
  const visibleToolbarGroups = useMemo(
    () => getVisibleToolbarGroups(controls),
    [controls]
  )

  const extraExtensions = useMemo(() => {
    const has = isControlAllowed(controls)
    return [
      ...(has('underline') ? [Underline] : []),
      ...(has('highlight') ? [Highlight] : []),
      ...(has('link') || has('unlink')
        ? [Link.configure({ HTMLAttributes: { target: '_blank' } })]
        : []),
    ]
  }, [controls])

  const richTextEditor = useEditor({
    editable: !readOnly,
    extensions: [
      starterKitConfig ? StarterKit.configure(starterKitConfig) : StarterKit,
      ...extraExtensions,
      Placeholder.configure({ placeholder: placeholder ?? '' }),
    ],
    content,
    onUpdate: ({ editor }) => {
      handleUpdate(editor.getText().length ? editor.getHTML() : '')
    },
    onBlur: handleOnBlur(handleBlur, fieldName),
  })

  useEffect(() => {
    if (richTextEditor?.getHTML() !== content) {
      richTextEditor?.commands.setContent(content as Content)
    }

    if (!richTextEditor?.getText().length && content?.length) {
      richTextEditor?.commands.setContent(content as Content)
    }
  }, [content, richTextEditor])

  return (
    <>
      <Box
        className={clsx(classes.EditorStyles, className)}
        data-testid={testId}
      >
        <MantineRichTextEditor
          editor={richTextEditor}
          aria-invalid={error}
          labels={labels}
          classNames={{
            root: clsx(classes.EditorRoot, {
              [classes.EditorRootWithAddOn]: bottomAddOn,
              [classes.EditorRootReadOnly]: readOnly,
            }),
            content: classes.EditorContent,
          }}
        >
          {!readOnly && (
            <MantineRichTextEditor.Toolbar sticky stickyOffset={80}>
              {visibleToolbarGroups.map((group) => (
                <MantineRichTextEditor.ControlsGroup key={group[0]}>
                  {group.map((c) => renderToolbarControl(c, c))}
                </MantineRichTextEditor.ControlsGroup>
              ))}
            </MantineRichTextEditor.Toolbar>
          )}
          <MantineRichTextEditor.Content />
        </MantineRichTextEditor>
      </Box>
      {bottomAddOn}
    </>
  )
}

export function RichTextEditor(props: Readonly<RichTextEditorProps>) {
  const editorKey = getEditorKey(props.controls, props.readOnly ?? false)
  return <RichTextEditorInner key={editorKey} {...props} />
}
